import { useLocation } from 'react-router-dom';
import { Box, NavLink } from 'ui';
import { IconWarning } from 'ui/Icons';

import classes from './NavLinks.module.css';

type BasicNavLinkRoutes = {
  path: string;
  label: string;
  enabled?: () => boolean;
  disabled?: () => boolean;
  opened?: boolean;
  isInvisible?: () => boolean;
  isActive?: (path?: string) => boolean;
  onNavigate?: () => void;
  navigates?: boolean;
  willSkip?: () => void;
  currentPath?: string;
  _currentPath?: string;
  isChildRoutesRendered?: () => boolean;
  onSkip?: () => void;
  onClick?: (callBack?: () => void) => void;
  error?: string;
};

interface NavLinkRoute extends BasicNavLinkRoutes {
  routes?: BasicNavLinkRoutes[];
  /**
   * Optional route string that can be used to split the current path from the base route
   */
  baseRoute?: string;
}
type NavLinkRoutes = NavLinkRoute[];

type NavLinkProps = {
  routes: NavLinkRoutes;
  setLocation?: (route: NavLinkRoute) => void;
  navigate?: (route: NavLinkRoute) => void;
  isLoading?: boolean;
};

function navLinkCreator(params: NavLinkRoute): NavLinkRoute {
  return {
    _currentPath: undefined,
    get currentPath() {
      if (!this._currentPath) return '';
      return this._currentPath;
    },
    set currentPath(pathName: string) {
      this._currentPath = pathName;
    },
    enabled() {
      return true;
    },
    isActive() {
      try {
        if (this.currentPath === undefined) return false;
        // Keep only thing to the right of the base route if it exists
        const pathToSplit =
          this.baseRoute && this.currentPath.includes(this.baseRoute)
            ? this.baseRoute + this.currentPath.split(this.baseRoute)[1]
            : this.currentPath;

        const pathList = pathToSplit.split(`/${this.path}`);

        return pathList.length > 1;
      } catch {
        return false;
      }
    },
    disabled() {
      return !this.enabled?.();
    },
    isInvisible: () => false,
    isChildRoutesRendered() {
      const hasRoutes = (this.routes && this.routes?.length > 0) ?? false;
      const isOpened = this.opened ?? false;
      const isInvisible = this.isInvisible?.() ?? false;
      return hasRoutes && isOpened && !isInvisible;
    },
    willSkip() {
      if (this.isActive?.() && this.isInvisible?.()) {
        if (this.onSkip) return this.onSkip();
      }
    },
    onClick(callBack?: () => void) {
      if (this?.disabled?.()) return undefined;
      if (this?.navigates === false) return undefined;
      if (this.onNavigate) return this.onNavigate();
      if (callBack) return callBack();
    },
    ...params,
  };
}

const NavLinks = ({ routes, setLocation, navigate, isLoading }: NavLinkProps) => {
  const location = useLocation();

  if (isLoading) return null;

  return (
    <Box h="100%">
      {routes?.map((route) => {
        route.currentPath = location.pathname;
        const isActive = route.isActive?.();
        if (route.isActive?.() && route.isInvisible?.()) {
          route.willSkip?.();
        }
        if (isActive && setLocation) {
          !isLoading && setLocation(route);
        }
        if (route.isInvisible?.()) return null;
        return (
          <NavLink
            rightSection={route.opened ? <></> : null}
            variant="filled"
            defaultOpened={false}
            label={route.label}
            key={route.label}
            active={isActive}
            opened={route.opened} // Hide right icon when no subRoutes
            disabled={route?.disabled?.()}
            onClick={() => {
              route.onClick?.(() => navigate?.(route));
            }}
            leftSection={route.error ? <IconWarning /> : undefined}
            classNames={{
              root: classes.NavLink,
              body: classes.NavLink__body,
              label: classes.NavLink__label,
              children: classes.NavLink__children,
              section: classes.NavLink__section,
            }}
            data-error={!!route.error}
          >
            {route.isChildRoutesRendered?.() && route.routes ? (
              <NavLinks routes={route?.routes} setLocation={setLocation} navigate={navigate} />
            ) : undefined}
          </NavLink>
        );
      })}
    </Box>
  );
};

export { navLinkCreator, NavLinks };
export type { BasicNavLinkRoutes, NavLinkRoute };
